/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  WbAutocomplete,
  useEnrichedIdentity,
} from '@agilelab/plugin-wb-platform';
import { GetEntitiesResponse } from '@mainstage/catalog-client';
import { RELATION_OWNED_BY } from '@mainstage/catalog-model';
import { useApi } from '@mainstage/core-plugin-api';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@mainstage/plugin-catalog-react';
import FormControl from '@material-ui/core/FormControl';
import { useCallback, useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { extractCustomProperties, isHidden } from '../../utils';
import { OwnedEntityPickerProps } from './schema';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

/**
 * The underlying component that is rendered in the form for the `OwnedEntityPicker`
 * field extension.
 *
 * @public
 */
export const OwnedEntityPicker = (props: OwnedEntityPickerProps) => {
  const {
    onChange,
    schema: { title = 'Entity', description = 'An entity from the catalog' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    formContext,
  } = props;

  const allowedKinds = uiSchema['ui:options']?.allowedKinds;
  const defaultKind = uiSchema['ui:options']?.defaultKind;
  const defaultNamespace = uiSchema['ui:options']?.defaultNamespace;
  const allowArbitraryValues =
    uiSchema['ui:options']?.allowArbitraryValues ?? true;
  const { ownedEntities, loading } = useOwnedEntities(allowedKinds);
  const customProps = extractCustomProperties(uiSchema);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  const entityRefs = ownedEntities?.items
    .map(e => humanizeEntityRef(e, { defaultKind, defaultNamespace }))
    .filter(n => n);

  // Handle changes with dependency manager support
  const onSelect = useCallback(
    (_: any, value: string | null) => {
      const newValue = value || '';

      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value: newValue,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(newValue);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <FormControl
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <WbAutocomplete
        id={idSchema?.$id}
        value={(formData as string) || ''}
        loading={loading}
        onChange={onSelect}
        options={entityRefs || []}
        autoSelect
        freeSolo={allowArbitraryValues}
        label={title}
        helperText={description}
        required={required}
        {...customProps}
      />
    </FormControl>
  );
};

/**
 * Takes the relevant parts of the Backstage identity, and translates them into
 * a list of entities which are owned by the user. Takes an optional parameter
 * to filter the entities based on allowedKinds
 *
 *
 * @param allowedKinds - Array of allowed kinds to filter the entities
 */
function useOwnedEntities(allowedKinds?: string[]): {
  loading: boolean;
  ownedEntities: GetEntitiesResponse | undefined;
} {
  const catalogApi = useApi(catalogApiRef);
  const { identity: enrichedIdentity, loading: identityLoading } =
    useEnrichedIdentity();

  const { loading, value: refs } = useAsync(async () => {
    if (!enrichedIdentity) {
      return undefined;
    }

    const catalogs = await catalogApi.getEntities(
      allowedKinds
        ? {
            filter: {
              kind: allowedKinds,
              [`relations.${RELATION_OWNED_BY}`]:
                enrichedIdentity.ownershipEntityRefs || [],
            },
          }
        : {
            filter: {
              [`relations.${RELATION_OWNED_BY}`]:
                enrichedIdentity.ownershipEntityRefs || [],
            },
          },
    );
    return catalogs;
  }, [enrichedIdentity, allowedKinds]);

  const ownedEntities = useMemo(() => {
    return refs;
  }, [refs]);

  return useMemo(
    () => ({ loading: loading || identityLoading, ownedEntities }),
    [loading, identityLoading, ownedEntities],
  );
}
